/**
 * RBAC (TZ §1.2) — huquq formati `{modul}.{resurs}.{amal}`.
 *
 * ⚠️ VAQTINCHALIK KO'PRIK. TZ ~340 atomik huquq va 16 rolni ta'riflaydi, lekin
 * jonli Go yadro hozir faqat `admins.role` satrini qaytaradi — huquqlar ro'yxati
 * yo'q. Shu sabab rol→huquq xaritasi HOZIRCHA klientda turadi.
 *
 * Bu XAVFSIZLIK CHEGARASI EMAS: klient faqat nimani KO'RSATISHNI hal qiladi.
 * Haqiqiy majburlash serverda (`RequireAdmin` + `RequireAdminRole`). Klient
 * xaritasi noto'g'ri bo'lsa — foydalanuvchi tugmani ko'radi, lekin API 403 beradi.
 *
 * A dan kerak (tz/admin/HOLAT.md): `GET /admin/me` → { username, roles[],
 * permissions[] } — shundan keyin bu xarita o'chiriladi va server manba bo'ladi.
 */

import type { AdminIdentity } from './auth'

/** Faza 1 uchun aniqlangan huquqlar. Ro'yxat modul qo'shilgan sari o'sadi. */
export const PERMISSIONS = {
  // A23 — Xodimlar va tashkilot
  STAFF_VIEW: 'staff.member.view',
  STAFF_CREATE: 'staff.member.create',
  STAFF_EDIT: 'staff.member.edit',
  STAFF_SUSPEND: 'staff.member.suspend',
  STAFF_OFFBOARD: 'staff.member.offboard',
  ROLE_VIEW: 'staff.role.view',
  ROLE_EDIT: 'staff.role.edit',
  PERMISSION_GRANT: 'staff.permission.grant',
  JIT_REQUEST: 'staff.jit.request',
  JIT_APPROVE: 'staff.jit.approve',

  // A18 — Audit (Faza 1 da faqat ko'rish)
  AUDIT_VIEW: 'security.audit.view',
  // A18.06 — four-eyes navbati. Ko'rish va tasdiqlash ALOHIDA huquq:
  // navbatni kuzatish (masalan auditor) tasdiqlash huquqini bermaydi.
  FOUR_EYES_VIEW: 'security.four_eyes.view',
  FOUR_EYES_APPROVE: 'security.four_eyes.approve',

  // A3 / A11 — mavjud endpointlar ustidan (X1 ni jonli tekshirish uchun)
  USERS_VIEW: 'users.list.view',
  USERS_BAN: 'users.account.ban',
  COUNTRIES_VIEW: 'geo.country.view',
  COUNTRIES_BLOCK: 'geo.country.block',
} as const

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS] | (string & {})

/** Hamma huquq — faqat `super_admin` uchun. */
const ALL = '*' as const

/**
 * Rol → huquqlar. Jonli bazadagi rol satrlari (`superadmin`) TZ nomlari
 * (`super_admin`) bilan bir xil emas — ikkalasi ham qabul qilinadi.
 */
export const ROLE_PERMISSIONS: Record<string, readonly string[]> = {
  superadmin: [ALL],
  super_admin: [ALL],

  platform_admin: [
    PERMISSIONS.STAFF_VIEW,
    PERMISSIONS.ROLE_VIEW,
    PERMISSIONS.AUDIT_VIEW,
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.COUNTRIES_VIEW,
    PERMISSIONS.COUNTRIES_BLOCK,
  ],

  security_officer: [
    PERMISSIONS.STAFF_VIEW,
    PERMISSIONS.ROLE_VIEW,
    PERMISSIONS.AUDIT_VIEW,
    PERMISSIONS.FOUR_EYES_VIEW,
    PERMISSIONS.FOUR_EYES_APPROVE,
    PERMISSIONS.JIT_APPROVE,
    PERMISSIONS.USERS_VIEW,
  ],

  moderator: [PERMISSIONS.USERS_VIEW, PERMISSIONS.JIT_REQUEST],

  // TZ §1.1: auditor — hamma narsani KO'RADI, hech narsani o'zgartira olmaydi.
  // TZ §1.1: auditor KO'RADI, lekin tasdiqlamaydi → FOUR_EYES_VIEW bor,
  // FOUR_EYES_APPROVE yo'q.
  auditor: [
    PERMISSIONS.STAFF_VIEW,
    PERMISSIONS.ROLE_VIEW,
    PERMISSIONS.AUDIT_VIEW,
    PERMISSIONS.FOUR_EYES_VIEW,
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.COUNTRIES_VIEW,
  ],
}

/**
 * TZ §1.1 — 16 rol. Xarita hozir ularning bir qismini biladi; qolganlari
 * server huquq katalogini bergach to'ldiriladi (A23.04 matritsasi shuni
 * ko'rsatadi — «qamrov» ustuni).
 */
export const TZ_ROLES = [
  'super_admin',
  'platform_admin',
  'security_officer',
  'moderation_lead',
  'moderator',
  'csam_specialist',
  'finance_admin',
  'finance_viewer',
  'support_agent',
  'support_lead',
  'growth_manager',
  'content_manager',
  'bot_reviewer',
  'verification_officer',
  'data_analyst',
  'auditor',
] as const

export type TzRole = (typeof TZ_ROLES)[number]

/** Huquq ro'yxatining to'liq (aniqlangan) to'plami. */
export const ALL_PERMISSIONS: readonly string[] = Object.values(PERMISSIONS)

export function permissionsOf(identity: AdminIdentity | null): readonly string[] {
  if (!identity) return []
  // Server manba: `GET /admin/me` huquqlarni bergan bo'lsa — o'shani ishlatamiz.
  if (identity.permissions?.length) return identity.permissions
  // Aks holda — vaqtinchalik klient rol→huquq ko'prigi (server-endpoint yo'q edi).
  const roles = identity.roles?.length ? identity.roles : [identity.role]
  const out = new Set<string>()
  for (const r of roles) {
    for (const p of ROLE_PERMISSIONS[r] ?? []) out.add(p)
  }
  return [...out]
}

export function can(identity: AdminIdentity | null, permission: Permission): boolean {
  if (!identity) return false
  const perms = permissionsOf(identity)
  return perms.includes(ALL) || perms.includes(permission)
}

/** TZ §1.1: `super_admin` 2 tadan ko'p bo'lmasligi kerak — UI ogohlantirishi uchun. */
export function isSuperAdmin(identity: AdminIdentity | null): boolean {
  if (!identity) return false
  const roles = identity.roles?.length ? identity.roles : [identity.role]
  return roles.some((r) => r === 'superadmin' || r === 'super_admin')
}
